import React, { useEffect, useRef, useState } from 'react';
import { Timer as TimerIcon } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

const Timer = ({ timeLimit, onTimeUp, onTick, showUI = true, inline = false }) => {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Callers pass fresh closures every render. Held in a ref, they no longer sit in
  // the countdown's deps — otherwise each render restarted the interval, and a burst
  // of renders under a second apart stalled the clock entirely.
  const onTimeUpRef = useRef(onTimeUp);
  const onTickRef = useRef(onTick);
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
    onTickRef.current = onTick;
  });

  useEffect(() => {
    setTimeLeft(timeLimit);
  }, [timeLimit]);

  useEffect(() => {
    onTickRef.current?.(timeLeft);
  }, [timeLeft]);

  useEffect(() => {
    if (timeLeft <= 0) {
      onTimeUpRef.current?.();
      return;
    }
    const intervalId = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours > 0 ? String(hours).padStart(2, '0') + ':' : ''}${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (!showUI) return null;

  const isUrgent = timeLeft <= 60;

  return (
    <div className={`${inline ? '' : 'fixed top-3 right-5 z-[1000]'} flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold shadow-md transition-colors duration-300 ${
      isUrgent
        ? 'bg-red-500 text-white'
        : isDark
          ? 'bg-white/10 text-white border border-white/20'
          : 'bg-white text-gray-700 border border-gray-200'
    }`}>
      <TimerIcon className={`w-4 h-4 ${isUrgent ? 'text-white animate-pulse' : isDark ? 'text-pink-400' : 'text-pink-500'}`} />
      <span className={isUrgent ? 'animate-pulse' : ''}>{formatTime(timeLeft)}</span>
    </div>
  );
};

export default Timer;
